/* --------------------------------------------------------------------------
   Composant : SidebarLayout
   Rôle      : Gère la barre latérale (desktop & mobile) + le contenu + navbar
   -------------------------------------------------------------------------- */

'use client'

// Importe les composants Headless UI nécessaires
import * as Headless from '@headlessui/react'
import React, { useState } from 'react'
// Importe le composant utilisé pour les boutons du navbar mobile
import { NavbarItem } from './navbar'
import logo from '../../img/logo-clotere.svg'

/* -------------------------------------------------------------------------- */
/* Icônes d’ouverture/fermeture du menu mobile                               */
/* -------------------------------------------------------------------------- */

// Icône d’ouverture (hamburger)
function OpenMenuIcon() {
    return (
        <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
        </svg>
    )
}

// Icône de fermeture (croix)
function CloseMenuIcon() {
    return (
        <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
        </svg>
    )
}

/* -------------------------------------------------------------------------- */
/* Composant : MobileSidebar (Drawer sur mobile)                              */
/* -------------------------------------------------------------------------- */

function MobileSidebar({ open, close, children }) {
  return (
    <Headless.Dialog
      as="div"
      open={open}
      onClose={close}
      className="lg:hidden fixed inset-0 z-50"
    >
      {/* Overlay gris : clique dessus = close() */}
      <Headless.DialogBackdrop
        className="fixed inset-0 bg-black/30 transition z-40"
        onClick={close}
      />

      {/* Sidebar */}
      <Headless.DialogPanel
        className="
          fixed inset-y-0 left-0 z-50
          w-full max-w-80 p-2
          transition duration-300 ease-in-out
          data-[closed]:-translate-x-full
        "
      >
        <div className="flex h-full flex-col rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          {/* Bouton de fermeture */}
          <div className="-mb-3 px-4 pt-3 flex justify-end">
            <button
              onClick={close}
              aria-label="Close navigation"
              className="flex items-center gap-2"
            >
              <CloseMenuIcon />
            </button>
          </div>

          {/* Contenu : on injecte onClick={close} sur les enfants cliquables */}
          <nav className="mt-4">
            {React.Children.map(children, (child) =>
              React.isValidElement(child)
                ? React.cloneElement(child, {
                    onClick: (...args) => {
                      // On garde l'onClick existant si présent
                      if (child.props.onClick) {
                        child.props.onClick(...args)
                      }
                      close()
                    },
                  })
                : child
            )}
          </nav>
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Composant : SidebarLayout (export principal)                               */
/* -------------------------------------------------------------------------- */

export function SidebarLayout({ navbar, sidebar, children }) {
    // État local : affichage ou non du sidebar mobile
    const [showSidebar, setShowSidebar] = useState(false)

    return (
        <div className="relative isolate flex min-h-svh w-full bg-white max-lg:flex-col lg:bg-zinc-100 dark:bg-zinc-900 dark:lg:bg-zinc-950">
            {/* Sidebar permanent sur desktop */}
            <div className="fixed inset-y-0 left-0 w-64 max-lg:hidden">{sidebar}</div>

            {/* Sidebar (drawer) sur mobile */}
            <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
                {sidebar}
            </MobileSidebar>


      {/* Navbar on mobile */}
      <header className="flex items-center px-4 lg:hidden">

        <div className="py-2.5">

          <NavbarItem onClick={() => setShowSidebar(true)} aria-label="Open navigation">
            <OpenMenuIcon />
          </NavbarItem>
        </div>
        <img src={logo} alt="clotere" className='size-16'/>
        <div className="min-w-0 flex-1">{navbar}</div>
      </header>

            {/* Contenu principal */}
            <main className="flex flex-1 flex-col pb-2 lg:min-w-0 lg:pl-64 lg:pr-2 lg:pt-2">
                <div className="grow p-6 bg-gradient-to-b from-slate-50 to-indigo-50 lg:rounded-lg lg:bg-white lg:p-10 lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10">
                    <div className="mx-auto max-w-6xl">{children}</div>
                </div>
            </main>
        </div>
    )
}
